"use client";

import * as React from "react";
import Image, { type StaticImageData } from "next/image";

import { Button } from "@/components/ui/button";
import { SectionHeading } from "@/components/ui/section-heading";
import { useBrandStore } from "@/stores/brand";
import { useInstagramMediaStore } from "@/stores/instagramMedia";
import { normalizeInstagramMediaPayload, type InstagramMediaPost } from "@/services/instagramMedia";
import product1 from "@/public/assets/sosmed1.png";
import product2 from "@/public/assets/sosmed2.png";
import product3 from "@/public/assets/sosmed3.png";

export function InstagramSection() {
  const { brand, isLoading } = useBrandStore();
  const { posts: mediaPosts, isLoading: isMediaLoading } = useInstagramMediaStore();
  const [brandInstagramMedia, setBrandInstagramMedia] = React.useState<InstagramMediaPost[]>([]);
  const [isBrandInstagramLoading, setIsBrandInstagramLoading] = React.useState(false);

  React.useEffect(() => {
    const instagramApi = brand?.attributes?.instagram_api as unknown;

    if (!instagramApi || typeof instagramApi !== "string") {
      setBrandInstagramMedia([]);
      return;
    }

    const token = instagramApi.trim();
    if (!token) {
      setBrandInstagramMedia([]);
      return;
    }

    let cancelled = false;
    setIsBrandInstagramLoading(true);

    fetch(
      `https://graph.instagram.com/me/media?fields=id,media_url,thumbnail_url,permalink,caption&access_token=${encodeURIComponent(
        token,
      )}`,
    )
      .then((response) => response.json())
      .then((payload) => {
        if (cancelled) return;
        const normalized = normalizeInstagramMediaPayload(payload);
        setBrandInstagramMedia(normalized);
      })
      .catch(() => {
        if (cancelled) return;
        setBrandInstagramMedia([]);
      })
      .finally(() => {
        if (cancelled) return;
        setIsBrandInstagramLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [brand]);

  const instagramHref = React.useMemo(() => {
    const fallback = "https://www.instagram.com/romanoindonesia/";
    const socialLinks = brand?.attributes?.social_links ?? [];

    for (const item of socialLinks) {
      const href = item?.url ?? null;
      if (!href) continue;
      const slug = item?.social_platform?.slug?.toLowerCase() ?? "";
      const url = href.toLowerCase();
      if (slug.includes("instagram") || url.includes("instagram.com")) return href;
    }

    return fallback;
  }, [brand]);

  const instagramLabel = React.useMemo(() => {
    try {
      const url = new URL(instagramHref);
      const segments = url.pathname.split("/").filter(Boolean);
      const handle = segments[0] ?? "";
      return handle ? `@${handle}` : "@romanoindonesia";
    } catch {
      const trimmed = instagramHref.trim();
      if (!trimmed) return "@romanoindonesia";
      const segments = trimmed.split("/").filter(Boolean);
      const last = segments[segments.length - 1] ?? "";
      if (!last) return "@romanoindonesia";
      return last.startsWith("@") ? last : `@${last}`;
    }
  }, [instagramHref]);

  const fallbackPosts: Array<{ key: string; src: StaticImageData; alt: string; href: string | null }> = [
    { key: "fallback-1", src: product1, alt: "Romano Instagram post 1", href: null },
    { key: "fallback-2", src: product2, alt: "Romano Instagram post 2", href: null },
    { key: "fallback-3", src: product3, alt: "Romano Instagram post 3", href: null },
    { key: "fallback-4", src: product3, alt: "Romano Instagram post 4", href: null },
  ];

  const posts = brandInstagramMedia.length
    ? brandInstagramMedia
    : mediaPosts.length
      ? mediaPosts
      : fallbackPosts;

  const isInstagramLoading =
    (isMediaLoading && mediaPosts.length === 0) ||
    (isBrandInstagramLoading && brandInstagramMedia.length === 0);

  if ((isLoading && !brand) || isInstagramLoading) {
    return (
      <section className="mx-auto w-full px-4 sm:px-6 lg:px-10">
        <div className="mx-auto animate-pulse">
          <div className="text-center">
            <div className="mx-auto h-5 w-48 rounded-lg bg-muted" />
            <div className="mx-auto mt-3 h-12 w-80 max-w-full rounded-xl bg-muted" />
          </div>

          <div className="mx-auto mt-10 grid max-w-6xl grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="relative aspect-3/4 overflow-hidden rounded-3xl bg-muted shadow-sm"
              />
            ))}
          </div>

          <div className="mt-10 flex justify-center">
            <div className="h-10 w-44 rounded-full bg-muted" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto w-full px-4 sm:px-6 lg:px-10">
      <SectionHeading eyebrow="Social Media" title="Follow Us On Instagram" />

      <div className="mx-auto mt-10 grid max-w-6xl grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
        {posts.map((post, index) => (
          <a
            key={post.key}
            href={post.href ?? instagramHref}
            target="_blank"
            rel="noopener noreferrer"
            className="block focus:outline-none"
          >
            <div className="relative aspect-3/4 overflow-hidden rounded-3xl bg-muted shadow-sm">
              <Image
                src={post.src}
                alt={post.alt}
                fill
                sizes="(min-width: 768px) 260px, 45vw"
                priority={index < 2}
                className="object-cover"
                draggable={false}
                unoptimized={typeof post.src === "string" && /^https?:\/\//i.test(post.src)}
              />
            </div>
          </a>
        ))}
      </div>

      <div className="mt-10 flex justify-center">
        <Button
          asChild
          className="rounded-full bg-primary px-8 text-[clamp(12px,1.05vw,16px)] tracking-[0.18em] text-background hover:bg-primary/90"
        >
          <a
            href={instagramHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            {instagramLabel}
          </a>
        </Button>
      </div>
    </section>
  );
}
